import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

/**
 * Laboratory catalogue self-service API. Mirrors pharmacyStock.js.
 */

export async function listLaboratories() {
  const { data } = await api.get(endpoints.laboratoryList)
  return unwrapList(data)
}

// Load the whole catalogue in one request (no page controls; rendered
// client-side). 1000 = paginator max_page_size; without page_size the list is
// silently capped at the 50-row default. See pharmacyStock.js for the rationale.
export async function listLabStock({ laboratoryId, search = '' } = {}) {
  const params = { page_size: 1000 }
  if (laboratoryId) params.laboratory = laboratoryId
  if (search?.trim()) params.search = search.trim()
  const { data } = await api.get(endpoints.labStock, { params })
  return unwrapList(data)
}

export async function downloadLabTemplate({ category = '' } = {}) {
  const params = {}
  if (category) params.category = category
  const res = await api.get(endpoints.labStockTemplate, { params, responseType: 'blob' })
  return res.data
}

export async function bulkUploadLab({ file, laboratoryId }) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('laboratory_id', String(laboratoryId))
  const { data } = await api.post(endpoints.labStockBulkUpload, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/**
 * Create a single new LaboratoryStock row (one test into a lab's
 * catalogue). Powers the "+ Add lab test" modal — the fast per-test
 * path vs the bulk CSV upload.
 *
 * `payload` fields:
 *   lab_test         — global LabTest id (required; picked via CatalogueAutocomplete)
 *   laboratory       — laboratory id (optional for lab staff — the server
 *                      forces this to the caller's active laboratory;
 *                      required for admins)
 *   price            — decimal, PGK
 *   turnaround_time  — short string, e.g. "30 min", "Same day"
 *   is_offered       — boolean, default true
 *   notes            — optional 255-char string
 *
 * Server 400 with a JSON error is bubbled up as an axios error — the
 * common one is UniqueConstraint(laboratory, lab_test) violation when
 * the test is already in the catalogue. The modal shows a friendly
 * "You already offer this test" for that specific error.
 */
export async function createLabStockRow(payload) {
  const { data } = await api.post(endpoints.labStock, payload)
  return data
}

export async function updateLabStockRow(id, patch) {
  const { data } = await api.patch(`${endpoints.labStock}${id}/`, patch)
  return data
}

export async function deleteLabStockRow(id) {
  await api.delete(`${endpoints.labStock}${id}/`)
}

/**
 * Upload (or replace) the illustrative image for a *global* LabTest.
 * Admin-only on the LabTestViewSet. Lab staff see the image via the
 * read-only `test_image_url` pass-through on LaboratoryStockSerializer.
 */
export async function setLabTestImage(testId, file) {
  const fd = new FormData()
  if (file) {
    fd.append('image', file)
  } else {
    fd.append('image', '')
  }
  const { data } = await api.patch(`${endpoints.labTestSearch}${testId}/`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}
